import { useCallback } from 'react';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { IconKebab } from '../../../ui/icon/IconKebab';
import { IconButton } from '../../../ui/input/IconButton';
import { DataTestId } from '../../../ui/layout/DataTestIdWrapper';
import { ResizableGrid } from '../../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../../ui/posts/PostCardGrid/PostCardGrid';
import { calculateGridCardHeight } from '../../../ui/posts/PostCardGrid/utils';
import { PostContextMenu } from '../../../ui/posts/PostContextMenu';
import { useIsRobot } from '../../../utils/RobotContext';
import { EventSource } from '../../../utils/analyticsService';
import { hasAvailableProducts } from '../../../utils/hasAvailableProducts';
import { useGridImageOptimizer } from '../../../utils/mds/useGridImageAlias';
import { useFavoritesEnabled } from '../../../utils/useFavoritesEnabled';
import { withPostCallbacks } from '../../post/withPostCallbacks';
import { BusinessGridBox } from './BusinessGridBox';
import { RobotBusinessGrid } from './RobotBusinessGrid';
import type { BusinessResizableGridProps } from './types';

const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.BUSINESS);

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

const UserBusinessGrid: React.FC<BusinessResizableGridProps> = ({
    items,
    businessId,
    user,
    isService = false,
    onLoadNext,
    onPostKebabClick,
}) => {
    const isFavoritesEnabled = useFavoritesEnabled();
    const { getAlias: getGridImageAliasForCard } = useGridImageOptimizer();

    const getItemHeightCallback = useCallback(
        (index: number, width: number) => {
            const postModel = items[index];

            if (!postModel || postModel.isAd) {
                return 0;
            }

            return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType, {
                withoutFooter: true,
                withProductsPreview: Boolean(hasAvailableProducts(postModel) && !isService),
            });
        },
        [items, isService],
    );

    const renderItem = useCallback(
        (
            index: number,
            columns: number,
            isVisible?: boolean,
            cardSize?: { width: number; height: number },
        ) => {
            const postModel = items[index];

            if (!postModel || postModel.isAd) {
                return null;
            }

            const imageAlias = getGridImageAliasForCard(cardSize?.width);

            return (
                <EnchancedPostGridCardCompact
                    imageAlias={imageAlias}
                    post={postModel}
                    index={index}
                    gridColumns={columns}
                    isVisible={isVisible}
                    showFooter={false}
                    additionalIcon={
                        <EnhancedPostContextMenu
                            index={index}
                            trigger={
                                <IconButton
                                    backgroundColor="rgba(0, 0, 0, 0.24)"
                                    color="var(--control-secondary-blur-color)"
                                    shape="rect"
                                    size="xs"
                                    dataTestId={DataTestId.POST_CARD_KEBAB_BUTTON}
                                >
                                    <IconKebab
                                        fontSize={16}
                                        dataTestId={DataTestId.POST_CARD_KEBAB}
                                    />
                                </IconButton>
                            }
                            onTriggerClick={onPostKebabClick}
                            post={postModel}
                            user={user}
                            fromBusinessId={businessId}
                            menuOptions={
                                isFavoritesEnabled ? { placement: 'bottom-start' } : undefined
                            }
                        />
                    }
                    hideReportedPosts={false}
                    isCompact
                    isShowStatus
                    withProductsPreview={!isService}
                    hasLogPostViewEvent
                />
            );
        },
        [
            items,
            onPostKebabClick,
            user,
            businessId,
            isService,
            getGridImageAliasForCard,
            isFavoritesEnabled,
        ],
    );

    if (!items.length) {
        return null;
    }

    return (
        <BusinessGridBox>
            <ResizableGrid
                minItemWidth={210}
                items={items}
                loadMore={onLoadNext}
                getItemHeight={getItemHeightCallback}
                renderItem={renderItem}
            />
        </BusinessGridBox>
    );
};

export const BusinessResizableGrid: React.FC<BusinessResizableGridProps> = (props) => {
    const { isRobot } = useIsRobot();

    // виртуализация и замеры DOM не оставляют роботу разметки
    return isRobot ? (
        <RobotBusinessGrid items={props.items} isService={props.isService} />
    ) : (
        <UserBusinessGrid {...props} />
    );
};
